import React, { PropsWithChildren, useCallback } from "react";
import { createClasses } from "@bloomberg/media-ui-core";
import styles from "./style.module.css";
import SortIcon from "~icons/SortIcon";
import type { TableColumnDefinition } from "~types/table";

type TableColumnProps<T> = {
  column: TableColumnDefinition<T>;
  className?: string;
  sortState?: "asc" | "desc" | null;
  onSort?: () => void;
};

export default function TableColumn<T>(
  props: PropsWithChildren<TableColumnProps<T>>
) {
  const { column, className, sortState = null, onSort } = props;

  const { align = "start", allowSorting, hidden = false } = column;

  const columnClass = createClasses([
    styles.column,
    className,
    {
      [styles.hiddenColumn]: hidden,
      [styles.start]: align === "start",
      [styles.end]: align === "end",
      [styles.noSort]: !allowSorting,
    },
  ]);

  const columnNode = (
    <th className={columnClass} scope="col" aria-sort="none">
      {props.children}
    </th>
  );

  let ariaSort: "ascending" | "descending" | "none";

  switch (sortState) {
    case "asc":
      ariaSort = "ascending";
      break;
    case "desc":
      ariaSort = "descending";
      break;
    default:
      ariaSort = "none";
  }

  let iconClass: string;

  switch (sortState) {
    case "asc":
      iconClass = styles.sortAsc;
      break;
    case "desc":
      iconClass = styles.sortDesc;
      break;
    default:
      iconClass = styles.sortNeutral;
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        onSort?.();
      }
    },
    [onSort]
  );

  if (allowSorting && onSort) {
    return (
      <th className={columnClass} scope="col" aria-sort={ariaSort}>
        <button
          type="button"
          onClick={onSort}
          onKeyDown={handleKeyDown}
          className={allowSorting && styles.sortableColumn}
        >
          <span>{props.children}</span>
          <span className="sr-only">
            {sortState ? `, sorted ${ariaSort}` : ", not sorted"}
          </span>
          <SortIcon className={iconClass} aria-hidden="true" />
        </button>
      </th>
    );
  }

  return columnNode;
}
